import React, { useState, useEffect } from 'react';
import {
    Bell, Mail, MessageSquare, MessageCircle,
    Settings, Menu, X, BarChart3, TrendingUp,
    History, Cog, BookOpen, Zap, User, LogOut,
    Wifi, FileText, Terminal, SearchCode
} from 'lucide-react';
import AnimatedBackground from './Background';
import { useWebSocket } from '../websocket/WebSocketContext';

export default function Layout({
    children,
    email,
    onLogout,
    activeTab,
    setActiveTab,
    showHistory,
    setShowHistory,
    showAppStats,
    setShowAppStats,
    showConfig,
    setShowConfig,
    showQueueStats,
    setShowQueueStats,
    showApiDocs,
    setShowApiDocs,
    showLogs,
    setShowLogs,
    showSmsTemplates,
    setShowSmsTemplates,
    showWhatsAppTemplates,
    setShowWhatsAppTemplates,
    showSearchById,
    setShowSearchById,
}) {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const { connected } = useWebSocket();

    useEffect(() => {
        if (!connected) setShowLogs(false);
    }, [connected, setShowLogs]);

    const tabs = [
        { id: 'push', label: 'Push Notifications', icon: Bell, color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-500/10' },
        { id: 'email', label: 'Email', icon: Mail, color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-500/10' },
        { id: 'sms', label: 'SMS', icon: MessageSquare, color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-500/10' },
        { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'from-emerald-500 to-teal-500', bgColor: 'bg-emerald-500/10' },
    ];

    const getConfigLabel = () => ({ push: 'FCM Config', email: 'Email Config', sms: 'SMS Config', whatsapp: 'WhatsApp Config' }[activeTab] || 'Configuration');
    const getHistoryLabel = () => ({ push: 'FCM History', email: 'Email History', sms: 'SMS History', whatsapp: 'WhatsApp History' }[activeTab] || 'History');

    const resetAllViews = () => {
        setShowHistory('');
        setShowConfig('');
        setShowAppStats(false);
        setShowQueueStats(false);
        setShowApiDocs(false);
        setShowLogs(false);
        if (setShowSmsTemplates) setShowSmsTemplates(false);
        if (setShowWhatsAppTemplates) setShowWhatsAppTemplates(false);
        if (setShowSearchById) setShowSearchById(false);
    };

    const handleConfigClick = () => {
        if (isNavigating) return;
        setIsNavigating(true);
        resetAllViews();
        const map = { push: 'fcm', email: 'email', sms: 'sms', whatsapp: 'whatsapp' };
        if (map[activeTab]) setShowConfig(map[activeTab]);
        setTimeout(() => setIsNavigating(false), 300);
    };

    const handleHistoryClick = () => {
        resetAllViews();
        const map = { push: 'fcm', email: 'email', sms: 'sms', whatsapp: 'whatsapp' };
        if (map[activeTab]) setShowHistory(map[activeTab]);
    };

    const handleTabSwitch = (tabId) => {
        setActiveTab(tabId);
        resetAllViews();
        setIsSidebarOpen(false);
    };

    return (
        // ROOT: h-screen + overflow-hidden locks the whole app to viewport
        <div className="h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] font-inter flex relative overflow-hidden">
            <AnimatedBackground />

            {/* Mobile overlay */}
            {isSidebarOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)} />
            )}

            {/* SIDEBAR — fixed height, scrollable internally */}
            <aside className={`fixed inset-y-0 left-0 w-72 sm:w-80 bg-black/20 backdrop-blur-xl border-r border-white/10 z-50 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 transition-transform duration-300 ease-in-out flex-shrink-0`}>
                {/* overflow-y-auto makes sidebar scroll independently */}
                <div className="p-4 sm:p-6 h-full flex flex-col overflow-y-auto">

                    {/* Mobile close */}
                    <div className="flex justify-end lg:hidden mb-4 flex-shrink-0">
                        <button onClick={() => setIsSidebarOpen(false)}
                            className="text-white/80 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Logo */}
                    <div className="flex items-center space-x-3 sm:space-x-4 mb-6 sm:mb-8 flex-shrink-0">
                        {connected ? (
                            <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-r from-green-500 to-emerald-500">
                                <Wifi className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                                <div className="absolute inset-0 rounded-xl sm:rounded-2xl animate-ping bg-green-400 opacity-20" />
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-white" />
                            </div>
                        ) : (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                                <Zap className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-lg sm:text-xl font-bold text-white">Notification Hub</h1>
                            <p className={`text-xs sm:text-sm transition-colors duration-300 ${connected ? 'text-green-400' : 'text-white/50'}`}>
                                Communication Dashboard
                            </p>
                        </div>
                    </div>

                    {/* Communication Types */}
                    <div className="mb-4 sm:mb-6 flex-shrink-0">
                        <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center">
                            <Settings className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-400" />
                            Communication Types
                        </h2>
                        <nav className="space-y-2">
                            {tabs.map((tab) => (
                                <button key={tab.id}
                                    onClick={(e) => { e.stopPropagation(); handleTabSwitch(tab.id); }}
                                    className={`w-full flex items-center space-x-3 p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 ${activeTab === tab.id
                                        ? `bg-gradient-to-r ${tab.color} text-white shadow-xl transform scale-105`
                                        : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                    <div className={`p-1.5 sm:p-2 rounded-lg sm:rounded-xl ${activeTab === tab.id ? 'bg-white/20' : tab.bgColor}`}>
                                        <tab.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </div>
                                    <span className="font-medium text-sm sm:text-base">{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Configuration */}
                    <div className="mb-4 sm:mb-6 pt-4 sm:pt-6 border-t border-white/10 flex-shrink-0">
                        <h3 className="text-xs sm:text-sm font-semibold text-white/80 mb-2 sm:mb-3 flex items-center">
                            <Cog className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                            Configuration
                        </h3>
                        <div className="space-y-2">
                            <button onClick={handleConfigClick} disabled={isNavigating}
                                className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showConfig ? 'bg-cyan-500/20 text-cyan-200' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-cyan-500/10">
                                    <Settings className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-400" />
                                </div>
                                <span className="font-medium text-xs sm:text-sm">{getConfigLabel()}</span>
                            </button>

                            {activeTab === 'sms' && (
                                <button onClick={() => { resetAllViews(); if (setShowSmsTemplates) setShowSmsTemplates(true); }}
                                    className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showSmsTemplates ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-500/30' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                    <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-indigo-500/10">
                                        <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-400" />
                                    </div>
                                    <span className="font-medium text-xs sm:text-sm">Manage Templates</span>
                                </button>
                            )}

                            {activeTab === 'whatsapp' && (
                                <button onClick={() => { resetAllViews(); if (setShowWhatsAppTemplates) setShowWhatsAppTemplates(true); }}
                                    className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showWhatsAppTemplates ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/30' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                    <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-emerald-500/10">
                                        <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                                    </div>
                                    <span className="font-medium text-xs sm:text-sm">Manage Templates</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Analytics & History */}
                    <div className="mb-4 sm:mb-6 pt-4 sm:pt-6 border-t border-white/10 flex-shrink-0">
                        <h3 className="text-xs sm:text-sm font-semibold text-white/80 mb-2 sm:mb-3 flex items-center">
                            <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                            Analytics & History
                        </h3>
                        <div className="space-y-2">
                            <button onClick={() => { resetAllViews(); setShowAppStats(true); }}
                                className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showAppStats ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-orange-500/10">
                                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-orange-400" />
                                </div>
                                <span className="font-medium text-xs sm:text-sm">App-Based Stats</span>
                            </button>

                            <button onClick={handleHistoryClick}
                                className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showHistory ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-indigo-500/10">
                                    <History className="w-3 h-3 sm:w-4 sm:h-4 text-indigo-400" />
                                </div>
                                <span className="font-medium text-xs sm:text-sm">{getHistoryLabel()}</span>
                            </button>

                            <button onClick={() => { resetAllViews(); if (setShowSearchById) setShowSearchById(true); }}
                                className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showSearchById ? 'bg-violet-500/20 text-violet-200 border border-violet-500/30' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-violet-500/10">
                                    <SearchCode className="w-3 h-3 sm:w-4 sm:h-4 text-violet-400" />
                                </div>
                                <span className="font-medium text-xs sm:text-sm">Search by ID</span>
                            </button>

                            <button onClick={() => { resetAllViews(); setShowApiDocs(true); }}
                                className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showApiDocs ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-green-500/10">
                                    <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                                </div>
                                <span className="font-medium text-xs sm:text-sm">API Documentation</span>
                            </button>

                            {connected && (
                                <button onClick={() => { resetAllViews(); setShowLogs(true); }}
                                    className={`w-full flex items-center space-x-3 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300 ${showLogs ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'text-white/60 hover:text-white hover:bg-white/5'}`}>
                                    <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-emerald-500/10">
                                        <Terminal className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                                    </div>
                                    <span className="font-medium text-xs sm:text-sm">Live Logs</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* User & Logout — pushed to bottom */}
                    <div className="mt-auto pt-4 border-t border-white/10 hidden lg:block flex-shrink-0">
                        <div className="flex items-center space-x-3 mb-4 px-3 py-2 bg-white/5 rounded-xl">
                            <User className="w-4 h-4 text-white/80 flex-shrink-0" />
                            <span className="text-sm text-white/80 truncate">{email}</span>
                        </div>
                        <button onClick={onLogout}
                            className="w-full flex items-center space-x-2 px-3 py-2 bg-red-500/20 text-red-200 rounded-xl hover:bg-red-500/30 transition-all duration-200">
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Logout</span>
                        </button>
                    </div>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="flex-1 flex flex-col h-screen lg:ml-0 relative z-10 overflow-hidden min-w-0">
                {/* Mobile header */}
                <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 z-40 lg:hidden flex-shrink-0">
                    <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4">
                        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="text-white focus:outline-none p-2 rounded-xl hover:bg-white/10 transition-colors">
                            <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                        <div className="flex items-center space-x-2 px-2 sm:px-3 py-1 bg-white/5 rounded-full">
                            <User className="w-3 h-3 sm:w-4 sm:h-4 text-white/80" />
                            <span className="text-xs sm:text-sm text-white/80 truncate max-w-24 sm:max-w-32">{email}</span>
                        </div>
                        <button onClick={onLogout}
                            className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-red-500/20 text-red-200 rounded-lg sm:rounded-xl hover:bg-red-500/30 transition-all duration-200">
                            <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline text-xs sm:text-sm">Logout</span>
                        </button>
                    </div>
                </header>

                {/* KEY FIX: overflow-y-auto for normal pages, overflow-hidden for Logs */}
                <main className={`flex-1 min-h-0 ${showLogs ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'}`}>
                    {children}
                </main>
            </div>
        </div>
    );
}